import { getCountryFlag } from '@home/_config/countries';
import { PR_CHANNEL_BRAND_COLORS, PR_CHANNEL_FALLBACK_COLORS, normalizeApiChannelNameToKey } from '@home/pages/studio/pr/prChannelColors';
import dataCommonFormat from '@/pages/home/utils/dbDataFormat/dataCommonFormat';
import type { IColumn } from '@/pages/dashboard/components/CommonTable/type';
import type { INestedMetricValue, TPlatformTotalRow } from './_type';

/** 获取渠道品牌色，支持自定义 colorByCode 覆盖，与 PrChannelColorLegend 保持一致 */
export function getChannelColor(
  channelName: string,
  fallbackIndex: number,
  colorByCode?: Record<string, string>,
): string {
  const key = normalizeApiChannelNameToKey(channelName);
  const colorMap = colorByCode && Object.keys(colorByCode).length > 0
    ? { ...PR_CHANNEL_BRAND_COLORS, ...colorByCode }
    : PR_CHANNEL_BRAND_COLORS;
  return (
    colorMap[key] ?? PR_CHANNEL_FALLBACK_COLORS[fallbackIndex % PR_CHANNEL_FALLBACK_COLORS.length]
  );
}

/** 判断原始值是否为嵌套的 { total, by_channel } 格式（支持 by_channel 为对象或数组） */
export function isNestedMetricValue(raw: unknown): raw is INestedMetricValue {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const o = raw as Record<string, unknown>;
  return typeof o.total === 'number' && o.by_channel != null && typeof o.by_channel === 'object';
}

/** 从 pageConfig 传入的列配置与内置列按 code 合并；不写 render 即可走默认富单元格。 */
export function columnOverrideMap(overrides: IColumn[] | undefined): Map<string, Partial<IColumn>> {
  const m = new Map<string, Partial<IColumn>>();
  (overrides || []).forEach((c) => {
    if (c?.code) m.set(c.code, c);
  });
  return m;
}

export function takeLabelEnCn(
  def: { nameEn: string; nameCn: string; },
  o?: Partial<IColumn> & { name?: string; },
): { nameEn: string; nameCn: string; } {
  if (!o) return { ...def };
  if (o.name != null && o.name !== '') {
    return { nameEn: String(o.name), nameCn: String(o.name) };
  }
  return {
    nameEn: o.nameEn != null && o.nameEn !== '' ? o.nameEn : def.nameEn,
    nameCn: o.nameCn != null && o.nameCn !== '' ? o.nameCn : def.nameCn,
  };
}

export function mergeFeatures(
  base: Record<string, unknown> | undefined,
  extra: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const m = { ...(base || {}), ...(extra || {}) };
  if (m.downloadFormat != null && m.downLoadFormat == null) {
    m.downLoadFormat = m.downloadFormat as string;
  }
  return m;
}

export function pickChannels(apiBlock: unknown): string[] {
  if (!apiBlock || typeof apiBlock !== 'object') return [];
  const inner = (apiBlock as { data?: { channels?: unknown; }; }).data;
  const ch = inner?.channels;
  return Array.isArray(ch) ? (ch as string[]) : [];
}

export function getLogoUrl(record: Record<string, unknown>): string | undefined {
  return (record.creator_avatar || record.outlet_logo || record.logo_url || record.media_logo) as string | undefined;
}

/**
 * 新：platform_totals 为 { code, value }[]；旧：{ "Facebook": 123, "Youtube": 456, ... } 平铺
 */
export function normalizePlatformTotalsFromApi(r: Record<string, unknown>): TPlatformTotalRow[] {
  const t = r.platform_totals;
  if (t == null) {
    return [];
  }

  if (Array.isArray(t)) {
    const rows: TPlatformTotalRow[] = [];
    t.forEach((item) => {
      if (item == null || typeof item !== 'object' || Array.isArray(item)) {
        return;
      }
      const o = item as Record<string, unknown>;
      const code = o.code != null && o.code !== '' ? String(o.code) : '';
      if (!code) {
        return;
      }
      const n = Number(o.value);
      if (!Number.isFinite(n)) {
        return;
      }
      rows.push({ code, value: n });
    });
    return rows;
  }

  if (typeof t === 'object') {
    const rows: TPlatformTotalRow[] = [];
    Object.keys(t as object).forEach((k) => {
      const v = (t as Record<string, unknown>)[k];
      if (v === null || v === undefined) {
        return;
      }
      if (typeof v === 'object' && !Array.isArray(v) && v !== null && 'value' in (v as object)) {
        const o = v as Record<string, unknown>;
        const code = o.code != null && String(o.code) !== '' ? String(o.code) : k;
        const n = Number(o.value);
        if (code && Number.isFinite(n)) {
          rows.push({ code, value: n });
        }
        return;
      }
      const n = Number(v);
      if (Number.isFinite(n)) {
        rows.push({ code: k, value: n });
      }
    });
    return rows;
  }
  return [];
}

/**
 * 首行 `dbh-pr-media-table__country-text`：主文案为接口 `media`；左侧国旗为 `country`（可含 2 位国码，如 "BR"），并兼容 `country_code` / `countryCode`。
 */
export function resolveMediaAndCountryFlag(r: Record<string, unknown>): { mediaText: string; flagUrl: string | null; } {
  const mediaText = r.media != null && r.media !== '' ? String(r.media) : '-';
  const codeRaw =
    (r.country as string) ||
    (r.country_code as string) ||
    (r.countryCode as string) ||
    (typeof r.country === 'string' && /^[a-zA-Z]{2}$/.test((r.country as string).trim())
      ? (r.country as string).trim()
      : '');
  const code = String(codeRaw || '').toLowerCase();
  const flag = code ? getCountryFlag(code) : null;
  return { mediaText, flagUrl: flag || null };
}

export function getMetricBarPercent(value: number, colMax: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (!Number.isFinite(colMax) || colMax <= 0) return 100;
  return Math.max(2, Math.min(100, (value / colMax) * 100));
}

export function formatValueForRule(raw: unknown, formatRule: string | undefined) {
  return dataCommonFormat({
    value: raw as any,
    formatRule: formatRule || 'thousandsInit',
    emptyValue: '-',
  }) as string;
}

/** 直播封面角标：duration 为「分钟」→ 先乘 60 再按总秒格式化 */
export function formatStreamDurationMinutes(min: unknown): string {
  const n = Number(min);
  if (!Number.isFinite(n) || n < 0) return '';
  return formatStreamDurationSeconds(n * 60);
}

/** Videos 封面角标：duration 为「秒」→ HH:MM:SS（Streams 勿用，见 formatStreamDurationMinutes） */
export function formatStreamDurationSeconds(sec: unknown): string {
  const n = Number(sec);
  if (!Number.isFinite(n) || n < 0) return '';
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = Math.floor(n % 60);
  return [h, m, s].map((x) => String(x).padStart(2, '0')).join(':');
}

/** 开播时间：ISO → 与截图类似的本地短格式 */
export function formatStreamTimeForDisplay(iso: unknown, isEN: boolean): string {
  if (iso == null || iso === '') return '';
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(isEN ? 'en-US' : 'zh-CN', { dateStyle: 'short', timeStyle: 'short' });
}

/** Posts / Streams 日期时间：`post_time`/`stream_time` → `2025-02-27 09:31PM`（本地 12 小时制，日期 yyyy-MM-dd，时分为两位，时间与 AM/PM 无空格） */
export function formatPostTimeForDisplay(raw: unknown): string {
  if (raw == null || raw === '') return '';
  const d = new Date(String(raw));
  if (Number.isNaN(d.getTime())) return String(raw);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h24 = d.getHours();
  const ampm = h24 >= 12 ? ' PM' : ' AM';
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  const h12Str = String(h12).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${day} ${h12Str}:${mi}${ampm}`;
}
